import {
  Component,
  computed,
  effect,
  inject,
  signal,
  ChangeDetectionStrategy,
} from "@angular/core";
import { Router } from "@angular/router";
import {
  FilterSet,
  FilterSetType,
} from "src/app/core/interfaces/filter-interface";
import { FiltersService } from "src/app/core/services/filters/filters-service";
import { Loader } from "src/app/shared/components/loader/loader";
import { FilterSection } from "src/app/shared/components/filter-section/filter-section";
import { Button } from "src/app/shared/components/utils/button/button";
import { JobSearchStore } from "../../store/job-search-store";
import { TranslocoModule } from "@jsverse/transloco";
import { FavoritesService } from "../../services/favorites/favorites-service";
import {
  CreateFavoriteFilterSearchRequestInterface,
  SearchType,
} from "src/app/core/interfaces/favorite-interface";
import { InputModal } from "src/app/shared/components/utils/input-modal/input-modal";
import { LanguageService } from "src/app/core/services/language-service";
import { FavoriteStore } from "src/app/features/store/favorite-store";
import { FavoriteSearchErrorModal } from "src/app/shared/components/favorite-search-error-modal/favorite-search-error-modal";
import { FilterSubsection } from "src/app/shared/components/filter-subsection/filter-subsection";
import { ErrorModalService } from "src/app/core/services/error-modal-service";

@Component({
  selector: "app-job-search-page",
  imports: [
    Loader,
    FilterSection,
    Button,
    TranslocoModule,
    InputModal,
    FavoriteSearchErrorModal,
    FilterSubsection,
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: "./job-search-page.html",
})
export class JobSearchPage {
  router = inject(Router);
  private readonly languageService = inject(LanguageService);
  filtersService = inject(FiltersService);
  favoritesService = inject(FavoritesService);
  favoriteStore = inject(FavoriteStore);
  store = inject(JobSearchStore);
  private readonly errorModalService = inject(ErrorModalService);

  filters = signal<FilterSet[]>([]);

  locationFiltersSelected = signal<FilterSet>(null);
  activitiesFiltersSelected = signal<FilterSet>(null);
  branchesFiltersSelected = signal<FilterSet>(null);
  locationSearchValueSelected = signal<string>("");
  branchesEmpty = signal<boolean>(true);
  activitiesEmpty = signal<boolean>(true);
  isActivityFilterUsed = signal<boolean>(false);
  locationEmpty = signal<boolean>(true);
  searchQuery = signal<string | null>(null);
  isAddToFavoriteModalOpen = signal(false);
  isFavoriteSearchSuccessSaved = signal<boolean>(false);

  branchesFilters = computed(() =>
    this.filters().find((f) => f.filterSetType === FilterSetType.Sector)
  );

  activitiesFilters = computed(() =>
    this.filters().find((f) => f.filterSetType === FilterSetType.Activity)
  );

  localisationFilters = computed(() =>
    this.filters().find(
      (f) => f.filterSetType === FilterSetType.GeographicalLocation
    )
  );

  onBranchesEmpty(isEmpty: boolean) {
    this.branchesEmpty.set(isEmpty);
  }

  onActivitiesEmpty(isEmpty: boolean) {
    this.activitiesEmpty.set(isEmpty);
  }

  onLocalisationsEmpty(isEmpty: boolean) {
    this.locationEmpty.set(isEmpty);
  }

  isFiltersEmpty = computed(
    () =>
      this.branchesEmpty() ||
      (this.activitiesEmpty() && this.isActivityFilterUsed()) ||
      this.locationEmpty()
  );

  constructor() {
    effect(() => {
      this.languageService.currentLanguage();
      this.loadFilters();
    });
  }

  checkAllBranchesFiltersAreDisableActivityFilter(): boolean {
    if (this.branchesFiltersSelected().filters.length === 0) {
      return true;
    }
    return this.branchesFiltersSelected().filters.every(
      (selectedFilter) =>
        selectedFilter.customProperties !== null &&
        "disableActivityFilter" in selectedFilter.customProperties &&
        selectedFilter.customProperties["disableActivityFilter"] === "true"
    );
  }

  updateFiltersSelected(filterUpdated: FilterSet) {
    this.isFavoriteSearchSuccessSaved.set(false);
    switch (filterUpdated.filterSetType) {
      case FilterSetType.Sector:
        this.branchesFiltersSelected.set(filterUpdated);
        if (this.checkAllBranchesFiltersAreDisableActivityFilter()) {
          this.isActivityFilterUsed.set(false);
        } else {
          this.isActivityFilterUsed.set(true);
        }
        break;
      case FilterSetType.Activity:
        this.activitiesFiltersSelected.set(filterUpdated);
        break;
      case FilterSetType.GeographicalLocation:
        this.locationFiltersSelected.set(filterUpdated);
        break;
    }

    this.store.setFiltersSelected(
      this.locationFiltersSelected(),
      this.branchesFiltersSelected(),
      this.activitiesFiltersSelected(),
      this.searchQuery()
    );
  }

  async saveSearch(searchName: string): Promise<void> {
    const createFavoriteFilterSearchRequest: CreateFavoriteFilterSearchRequestInterface =
      {
        name: searchName,
        searchType: SearchType.JobSearch,
        research: {
          selectedFilters: [
            this.locationFiltersSelected(),
            this.branchesFiltersSelected(),
            this.activitiesFiltersSelected(),
          ],
          locationSearchValue: this.searchQuery(),
        },
      };

    try {
      await this.favoritesService.saveSearch(createFavoriteFilterSearchRequest);
    } catch {
      this.favoriteStore.setFavoriteSearchesErrorModalOpen(true);
    } finally {
      this.isFavoriteSearchSuccessSaved.set(true);
      this.isAddToFavoriteModalOpen.set(false);
    }
  }

  startResearch(): void {
    this.store.setFiltersSelected(
      this.locationFiltersSelected(),
      this.branchesFiltersSelected(),
      this.isActivityFilterUsed() ? this.activitiesFiltersSelected() : null,
      this.searchQuery()
    );
    this.router.navigate(["/mobility/jobs-results"]);
  }

  loading = signal<boolean>(false);

  async loadFilters(): Promise<void> {
    this.loading.set(true);
    try {
      await this.store.loadJobFilters();
      this.filters.set([
        this.store.activitiesFilters(),
        this.store.branchesFilters(),
        this.store.locationFilters(),
      ]);

      if (this.store.activitiesFiltersSelected()) {
        this.activitiesFiltersSelected.set(
          this.store.activitiesFiltersSelected()
        );
      }
      if (this.store.locationFiltersSelected()) {
        this.locationFiltersSelected.set(this.store.locationFiltersSelected());
      }
      if (this.store.branchesFiltersSelected()) {
        this.branchesFiltersSelected.set(this.store.branchesFiltersSelected());
      }
      if (
        this.store.locationSearchValueSelected() !== undefined &&
        this.store.locationSearchValueSelected() !== ""
      ) {
        this.locationSearchValueSelected.set(
          this.store.locationSearchValueSelected()
        );
      }
    } catch (error) {
      if (this.errorModalService.shouldOpenErrorModal(error)) {
        this.errorModalService.openErrorModal();
      }
      this.filters.set([]);
    } finally {
      this.loading.set(false);
    }
  }

  handleSearchInput(query: string): void {
    this.searchQuery.set(query);
    if (query.length === 0) {
      this.searchQuery.set(null);
    }
  }

  openAddFavoriteModal(): void {
    this.isAddToFavoriteModalOpen.set(true);
  }

  closeAddFavoriteModal(): void {
    this.isAddToFavoriteModalOpen.set(false);
  }
}
